import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import api from "../lib/api";
import Seo from "../seo/Seo";
import { buildArticleGraph, getOrigin } from "../seo/schema";
import Navbar from "../components/site/Navbar";
import Footer from "../components/site/Footer";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    let active = true;
    setStatus("loading");
    api
      .get(`/posts/by-slug/${slug}`)
      .then(({ data }) => {
        if (active) {
          setPost(data);
          setStatus("ok");
        }
      })
      .catch(() => active && setStatus("notfound"));
    window.scrollTo(0, 0);
    return () => {
      active = false;
    };
  }, [slug]);

  return (
    <div className="bg-obsidian min-h-screen">
      <Navbar />

      {status === "ok" && post && (
        <>
          <Seo
            title={`${post.title} — Amiran Azaladze`}
            description={post.excerpt}
            path={`/blog/${post.slug}`}
            type="article"
            image={post.cover_image || "/og-image.jpg"}
            article={{
              publishedTime: post.created_at,
              modifiedTime: post.updated_at || post.created_at,
              tag: post.tag,
            }}
            jsonLd={buildArticleGraph(post, getOrigin())}
          />
          <article className="relative z-10 max-w-3xl mx-auto px-6 pt-32 pb-24">
            <Link
              to="/#blog"
              data-testid="blogpost-back"
              className="flex w-fit items-center gap-2 text-sm text-ink-muted hover:text-amber transition-colors mb-8"
            >
              <ArrowLeft size={15} /> Back to Blog
            </Link>
            <span className="inline-block rounded-full border border-amber/30 px-3 py-1 text-[0.68rem] uppercase tracking-[0.14em] text-amber mb-5">
              {post.tag}
            </span>
            <h1
              data-testid="blogpost-title"
              className="font-display font-bold text-3xl sm:text-4xl md:text-5xl leading-tight tracking-tight mb-4"
            >
              {post.title}
            </h1>
            <div className="text-sm text-ink-muted mb-10">
              {post.author} · {post.read_time}
            </div>
            <div className="prose-blog" dangerouslySetInnerHTML={{ __html: post.body }} />
          </article>
        </>
      )}

      {status === "loading" && (
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="h-8 w-8 rounded-full border-2 border-amber border-t-transparent animate-spin" />
        </div>
      )}

      {status === "notfound" && (
        <>
          <Seo
            title="Article not found — Amiran Azaladze"
            description="The article you are looking for could not be found."
            path={`/blog/${slug}`}
            noindex
          />
          <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-6 pt-24">
            <h1 className="font-display text-4xl font-bold mb-4">Article not found</h1>
            <Link to="/#blog" className="text-amber hover:underline">
              Back to the blog
            </Link>
          </div>
        </>
      )}

      <Footer />
    </div>
  );
}
